/**
 * Header Component - SENTINEL branding, domain filter chips, edge status indicator
 */

import { useCallback, useState } from 'react'
import type { Domain, EdgeStatus } from '../../types'
import { DOMAIN_CONFIG } from '../../types'

interface HeaderProps {
  activeDomainFilter: Domain | null
  onDomainFilterChange: (domain: Domain | null) => void
  selectedAsset: string | null
  edgeStatus: EdgeStatus | null
  onDDILToggle: (level: number) => void
  onTheaterSetup: () => void
  radioVisible?: boolean
  radioAvailable?: boolean
  onRadioToggle?: () => void
}

const DOMAIN_ENTRIES = Object.entries(DOMAIN_CONFIG) as [Domain, typeof DOMAIN_CONFIG[Domain]][]

export function Header({
  activeDomainFilter,
  onDomainFilterChange,
  selectedAsset,
  edgeStatus,
  onDDILToggle,
  onTheaterSetup,
  radioVisible,
  radioAvailable,
  onRadioToggle,
}: HeaderProps) {
  const [showRadioWarning, setShowRadioWarning] = useState(false)
  const handleDomainClick = useCallback(
    (domain: Domain) => {
      onDomainFilterChange(activeDomainFilter === domain ? null : domain)
    },
    [activeDomainFilter, onDomainFilterChange]
  )

  const connectivity = edgeStatus?.connectivity ?? 'FULL'
  const ddilLevel = edgeStatus?.ddil_level ?? 0

  return (
    <header className="border-b border-term-border bg-term-bg px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        {/* Logo/Title - Left */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-term-primary">SENTINEL</span>
            <span className="text-lg font-semibold text-term-text">C2 Edge Intelligence</span>
          </div>
          {selectedAsset && (
            <span className="text-term-textDim text-sm">
              | <span className="text-term-text font-medium">{selectedAsset}</span>
            </span>
          )}
        </div>

        {/* Domain Filter Chips - Center */}
        <div className="flex items-center gap-1.5">
          {DOMAIN_ENTRIES.map(([domain, config]) => (
            <button
              key={domain}
              onClick={() => handleDomainClick(domain)}
              className={`domain-chip ${
                activeDomainFilter === domain ? 'domain-chip-active' : 'opacity-60 hover:opacity-100'
              }`}
              style={{
                backgroundColor:
                  activeDomainFilter === domain ? `${config.color}30` : `${config.color}15`,
                borderColor:
                  activeDomainFilter === domain ? config.color : `${config.color}40`,
                color: config.color,
                ...(activeDomainFilter === domain
                  ? { boxShadow: `0 0 8px ${config.color}25` }
                  : {}),
              }}
              title={config.label}
            >
              {config.label}
            </button>
          ))}
          {activeDomainFilter && (
            <button
              onClick={() => onDomainFilterChange(null)}
              className="px-2 py-1 text-xs text-term-textDim hover:text-term-text transition-colors"
              title="Clear filter"
            >
              x
            </button>
          )}
        </div>

        {/* Radio + Theater + Edge Status + DDIL Toggle - Right */}
        <div className="flex items-center gap-3 shrink-0">
          {onRadioToggle && (
            <div className="relative">
              <button
                onClick={() => {
                  if (radioAvailable) {
                    onRadioToggle()
                  } else {
                    setShowRadioWarning(true)
                    setTimeout(() => setShowRadioWarning(false), 4000)
                  }
                }}
                className={`px-3 py-1.5 text-xs font-bold uppercase rounded border transition-colors ${
                  !radioAvailable
                    ? 'border-term-border/30 bg-term-bgDark text-term-textDim/40 cursor-not-allowed'
                    : radioVisible
                      ? 'border-term-error/60 bg-term-error/20 text-term-error hover:bg-term-error/30'
                      : 'border-term-border bg-term-bgLight text-term-textDim hover:border-term-error/40 hover:text-term-error hover:bg-term-error/10'
                }`}
                title={
                  !radioAvailable
                    ? 'Radio server offline — run: cd radio && source .venv/bin/activate && python radio_server.py'
                    : radioVisible
                      ? 'Hide Radio Monitor'
                      : 'Show Radio Monitor'
                }
              >
                Radio
              </button>
              {showRadioWarning && !radioAvailable && (
                <div className="absolute top-full right-0 mt-2 w-72 p-3 bg-term-bgDark border border-term-warning/50 rounded shadow-lg z-50">
                  <p className="text-xs text-term-warning font-bold mb-1">Radio Server Offline</p>
                  <p className="text-[11px] text-term-textDim leading-relaxed">
                    Start the local transcription server:
                  </p>
                  <code className="block mt-1.5 px-2 py-1 text-[10px] bg-term-bg border border-term-border rounded text-term-text">
                    cd radio && source .venv/bin/activate && python radio_server.py
                  </code>
                </div>
              )}
            </div>
          )}
          <button
            onClick={onTheaterSetup}
            className="px-3 py-1.5 text-xs font-bold uppercase rounded border border-term-primary/40 bg-term-primary/10 text-term-primary hover:bg-term-primary/20 hover:border-term-primary/60 transition-colors"
          >
            Theater
          </button>
          <div className="flex items-center gap-2">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                connectivity === 'FULL'
                  ? 'bg-emerald-400'
                  : connectivity === 'DEGRADED'
                    ? 'bg-amber-400 animate-pulse'
                    : 'bg-red-400 animate-pulse'
              }`}
            />
            <span
              className={`text-xs font-medium ${
                connectivity === 'FULL'
                  ? 'text-emerald-400'
                  : connectivity === 'DEGRADED'
                    ? 'text-amber-400'
                    : 'text-red-400'
              }`}
            >
              {connectivity}
            </span>
          </div>
          <select
            value={ddilLevel}
            onChange={(e) => onDDILToggle(parseInt(e.target.value))}
            className="text-xs bg-term-bgDark border border-term-border rounded px-2 py-1 text-term-text focus:outline-none focus:border-term-primary"
            title="DDIL Level"
          >
            <option value={0}>DDIL-0: Full</option>
            <option value={1}>DDIL-1: Degraded</option>
            <option value={2}>DDIL-2: Intermittent</option>
            <option value={3}>DDIL-3: Denied</option>
            <option value={4}>DDIL-4: Isolated</option>
          </select>
        </div>
      </div>
    </header>
  )
}
